import { AppService } from './../../../../../core/app.service';
import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIcon } from "@angular/material/icon";
import { ActivatedRoute, Router } from '@angular/router';
import { ExpressionBesoinRes } from '../../../../../data/http/responses/expression.res';
import { ExpressionBesoinService } from '../../../../../services/expressionbesoin.service';
import { LoaderComponent } from "../../../../../components/ui/loader/loader.component";
import { LigneBudgetaireService } from '../../../../../services/lignebudgetaire/lignebudgetaire.service';
import { StructureService } from '../../../../../services/StructureService';
import { StructureResponse } from '../../../../../data/http/responses/strcuture.res';
import { LigneBudgetaireResponse } from '../../../../../data/http/responses/lignebudgetaire.res';
import { Input } from '../../../../../components/forms/input/input';
import { RegroupementExpressionBesoinService } from '../../../../../services/regroupementexpressionbesoin.service';
import { toSnakeCaseUpper } from '../../../../../utils/helpers';
import { RegroupeExpressionBesoinReq } from '../../../../../data/http/requests/regroupementexpressionbesoin.req';
import { routeAlias } from '../../../../../../routesalias';
import { ActiviteService } from '../../../../../services/activite/activite.service';
import { SelectComponent } from '../../../../../components/forms/select/select.component';
import { SelectOption } from '../../../../../data/schemas/selectoption.schema';
import { UiButtonComponent } from '../../../../../components/button/button.component';

@Component({
  selector: 'app-add',
  imports: [MatIcon, ReactiveFormsModule, LoaderComponent, Input, SelectComponent, UiButtonComponent],
  templateUrl: './add.component.html',
  styleUrl: './add.component.css'
})
export class AddRegroupementExpressionBesoinPage implements OnInit {

  public isLoading: boolean = false;
  public loadingExpressions: boolean = false;
  public loadingStructures: boolean = false;
  public loadingLignesBudgetaires: boolean = false;

  public expressionsBesoins: ExpressionBesoinRes[] = [];
  public regroupementExpressionBesoinForm: FormGroup = new FormGroup({});
  public structureData: SelectOption[] = [];
  public ligneBudgetaires: SelectOption[] = [];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private AppService: AppService,
    private expressionBesoinService: ExpressionBesoinService,
    private ligneBudgetaireService: LigneBudgetaireService,
    private structureService: StructureService,
    private regroupementExpressionBesoinService: RegroupementExpressionBesoinService,
    private activiteService: ActiviteService
  ) {
    this.regroupementExpressionBesoinForm = this.fb.group({
      expressionBesoinSelectionnees: this.fb.array([]),
      applicationId: this.AppService.getAppId()!,
      complementaryInfo: this.fb.group({
        libelle: [''],
        description: [''],
        supervisor: [''],
        structureResponsable: [''],
        montantEstimatif: [''],
        imputationBudgetaire: [''],
        coutPrevuN1: [''],
        previsionDecaissementN2: [''],
        datePrevision: [''],
        dureePrevisionExecutionMarche: [''],
      })
    });
  }

  ngOnInit(): void {
    this.loadExpressionBesoins();
    this.loadLigneBudgetaire();
    this.loadStructures();
    this.addActivity();
  }

  get activitesFormArray(): FormArray {
    return this.regroupementExpressionBesoinForm.get('expressionBesoinSelectionnees') as FormArray;
  }

  get complementaryInfoFormGroup(): FormGroup {
    return this.regroupementExpressionBesoinForm.get('complementaryInfo') as FormGroup;
  }

  addActivity(): void {
    this.activitesFormArray.push(this.fb.control('', Validators.required));
  }

  removeActivity(index: number): void {
    if (this.activitesFormArray.length > 1) {
      this.activitesFormArray.removeAt(index);
    }
  }

  getAvailableActivities(index: number): ExpressionBesoinRes[] {
    const selectedIds = this.activitesFormArray.value;
    const currentId = this.activitesFormArray.at(index).value;
    return this.expressionsBesoins.filter(eb =>
      !selectedIds.includes(eb.id) || eb.id === currentId
    );
  }

  getAvailableActivitiesAsOptions(index: number): SelectOption[] {
    return this.getAvailableActivities(index).map(eb => ({
      value: eb.id as string,
      label: (eb as any).titre ?? `Expression #${eb.id}`
    }));
  }

  loadExpressionBesoins(): void {
    this.loadingExpressions = true;
    this.expressionBesoinService.getAllByAppId(this.AppService.getAppId()!).subscribe({
      next: (response: any) => {
        this.expressionsBesoins = response.data;
        this.loadingExpressions = false;
      },
      error: (error) => {
        console.error('Erreur lors du chargement des expressions de besoin :', error);
        this.loadingExpressions = false;
      }
    });
  }

  loadStructures(): void {
    this.loadingStructures = true;
    this.structureService.getAllByAppId(this.AppService.getAppId()!).subscribe({
      next: (response: any) => {
        this.structureData = response.data.map((item: StructureResponse) => ({
          value: item.code,
          label: item?.code
        }));
        this.loadingStructures = false;
      },
      error: (error) => {
        console.error('Erreur lors du chargement des structures :', error);
        this.loadingStructures = false;
      }
    });
  }

  loadLigneBudgetaire(): void {
    this.loadingLignesBudgetaires = true;
    this.ligneBudgetaireService.getAllByAppId(this.AppService.getAppId()!).subscribe({
      next: (response: any) => {
        this.ligneBudgetaires = response.data.map((item: LigneBudgetaireResponse) => ({
          value: item.libelle,
          label: item?.libelle
        }));
        this.loadingLignesBudgetaires = false;
      },
      error: (error) => {
        console.error('Erreur lors du chargement des lignes budgétaires :', error);
        this.loadingLignesBudgetaires = false;
      }
    });
  }

  onSubmit(): void {
    if (this.regroupementExpressionBesoinForm.valid) {
      const caracteristiquesArray: { code: string; valeur: any }[] = [];
      const excluded = ['libelle', 'description'];
      Object.keys(this.regroupementExpressionBesoinForm.value.complementaryInfo).forEach(key => {
        if (!excluded.includes(key)) {
          caracteristiquesArray.push({
            code: toSnakeCaseUpper(key),
            valeur: this.regroupementExpressionBesoinForm.value.complementaryInfo[key] ?? ''
          });
        }
      });

      this.isLoading = true;
      const requestBody: RegroupeExpressionBesoinReq = {
        applicationId: this.regroupementExpressionBesoinForm.value.applicationId,
        regroupements: this.regroupementExpressionBesoinForm.value.expressionBesoinSelectionnees,
        typeActivite: 'MARCHE',
        titre: this.regroupementExpressionBesoinForm.value.complementaryInfo.libelle,
        objectif: this.regroupementExpressionBesoinForm.value.complementaryInfo.description,
        caracteristiques: caracteristiquesArray
      };

      this.regroupementExpressionBesoinService.create(requestBody).subscribe({
        next: () => {
          this.isLoading = false;
          this.router.navigate([
            routeAlias.marchees.home,
            routeAlias.marchees.expression_besoin.home,
          ]);
        },
        error: (error) => {
          console.error('Erreur lors de la création du regroupement :', error);
          this.isLoading = false;
        }
      });
    } else {
      console.error('Le formulaire est invalide');
      this.regroupementExpressionBesoinForm.markAllAsTouched();
    }
  }
}